import { type FC, useEffect, useId } from 'react'
import { useIntl } from 'react-intl'

import { FormControl, FormHelperText, FormLabel, Input } from 'base-ui'

import { type ContactFormState } from '../../../contact-form'
import { type OpenableControls } from '../../../generic-hooks'
import { useGlobalConfigContext } from '../../../global-config'
import { useApiOrdersSwrGetOrders } from '../../../order'
import { directContactFormAccessorGetSubjects } from '../../model/directContactFormAccessorGetSubjects'
import { directContactFormAccessorIsProductType } from '../../model/directContactFormAccessorIsProductType'

import { useDirectFormDialogContext } from './DirectContactFormDialogContext'
import { DirectContactFormOrderInputFieldLoggedIn } from './DirectContactFormOrderInputFieldLoggedIn'

export const DirectContactFormOrderInputField: FC<{
  formState: ContactFormState
  isLoggedIn: boolean
  productSelectionModalControls: OpenableControls
}> = ({ formState, isLoggedIn, productSelectionModalControls }) => {
  const intl = useIntl()
  const { tenant } = useGlobalConfigContext()

  const orderNumberId = useId()
  const { productList, subOrderNumber } = useDirectFormDialogContext()

  const { data, error } = useApiOrdersSwrGetOrders({
    itemsPerPage: 6,
  })

  const { orderType } = directContactFormAccessorGetSubjects({
    intl,
    option: formState.model.subject,
    tenant,
  })

  useEffect(() => {
    if (isLoggedIn && !error && subOrderNumber) {
      formState.setValue(`${subOrderNumber}-1`, formState.model, 'orderId')
      const productCode = [...productList].map((item) => item.pzn).join(', ')
      if (productCode) {
        formState.setValue(productCode, formState.model, 'productCode')
      }
    }
  }, [error, formState, isLoggedIn, productList, subOrderNumber, intl])

  if (
    (!isLoggedIn && orderType) ||
    (isLoggedIn && orderType && (data?.orders?.length === 0 || error))
  ) {
    return (
      <FormControl
        className="mb-2"
        data-qa-id="contact-form-orderId-field"
        onChange={(event): void => {
          formState.makeInputProps('orderId').onChange(event)
        }}
        value={formState.model.orderId}
      >
        <FormLabel htmlFor={orderNumberId}>
          {intl.formatMessage({
            id: 'general.label.ordernumber',
          })}
        </FormLabel>
        <Input
          id={orderNumberId}
          slotProps={{
            input: {
              'data-qa-id': 'contact-form-orderId',
            },
          }}
        />
        {formState.model.validator.getFirstErrorFor('orderId') && (
          <FormHelperText data-qa-id="contact-form-orderId-error-msg">
            {formState.model.validator.getFirstErrorFor('orderId')}
          </FormHelperText>
        )}
      </FormControl>
    )
  }

  if (isLoggedIn && orderType) {
    return (
      <DirectContactFormOrderInputFieldLoggedIn
        formState={formState}
        isProductType={directContactFormAccessorIsProductType}
        productSelectionModalControls={productSelectionModalControls}
      />
    )
  }

  return null
}
